import React, { useState, useEffect } from 'react';
import { TabContent, ConversionTable, InputWrapper, InputGroup, GradientInput, InputLabel } from '../../pages/tools/ToolsPage.components';
import { volumeConversionFactors, formatNumber } from './conversionUtils';

const VolumeConverter: React.FC = () => {
    const [cubicMeter, setCubicMeter] = useState('1');
    const [liter, setLiter] = useState('');
    const [gallons, setGallons] = useState('');
    const [cubicFeet, setCubicFeet] = useState('');
    const [barrel, setBarrel] = useState('');

    // Initialize conversions on mount
    useEffect(() => {
        const defaultValue = 1;
        setLiter(formatNumber(defaultValue * volumeConversionFactors.liter, 6));
        setGallons(formatNumber(defaultValue * volumeConversionFactors.gallons, 6));
        setCubicFeet(formatNumber(defaultValue * volumeConversionFactors.cubicFeet, 6));
        setBarrel(formatNumber(defaultValue * volumeConversionFactors.barrel, 6));
    }, []);

    const handleCubicMeterChange = (value: string) => {
        setCubicMeter(value);
        if (value === '') {
            setLiter('');
            setGallons('');
            setCubicFeet('');
            setBarrel('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setLiter(formatNumber(numValue * volumeConversionFactors.liter, 6));
            setGallons(formatNumber(numValue * volumeConversionFactors.gallons, 6));
            setCubicFeet(formatNumber(numValue * volumeConversionFactors.cubicFeet, 6));
            setBarrel(formatNumber(numValue * volumeConversionFactors.barrel, 6));
        }
    };

    const handleLiterChange = (value: string) => {
        setLiter(value);
        if (value === '') {
            setCubicMeter('');
            setGallons('');
            setCubicFeet('');
            setBarrel('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / volumeConversionFactors.liter;
            setCubicMeter(formatNumber(baseValue, 6));
            setGallons(formatNumber(baseValue * volumeConversionFactors.gallons, 6));
            setCubicFeet(formatNumber(baseValue * volumeConversionFactors.cubicFeet, 6));
            setBarrel(formatNumber(baseValue * volumeConversionFactors.barrel, 6));
        }
    };

    const handleGallonsChange = (value: string) => {
        setGallons(value);
        if (value === '') {
            setCubicMeter('');
            setLiter('');
            setCubicFeet('');
            setBarrel('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / volumeConversionFactors.gallons;
            setCubicMeter(formatNumber(baseValue, 6));
            setLiter(formatNumber(baseValue * volumeConversionFactors.liter, 6));
            setCubicFeet(formatNumber(baseValue * volumeConversionFactors.cubicFeet, 6));
            setBarrel(formatNumber(baseValue * volumeConversionFactors.barrel, 6));
        }
    };

    const handleCubicFeetChange = (value: string) => {
        setCubicFeet(value);
        if (value === '') {
            setCubicMeter('');
            setLiter('');
            setGallons('');
            setBarrel('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / volumeConversionFactors.cubicFeet;
            setCubicMeter(formatNumber(baseValue, 6));
            setLiter(formatNumber(baseValue * volumeConversionFactors.liter, 6));
            setGallons(formatNumber(baseValue * volumeConversionFactors.gallons, 6));
            setBarrel(formatNumber(baseValue * volumeConversionFactors.barrel, 6));
        }
    };

    const handleBarrelChange = (value: string) => {
        setBarrel(value);
        if (value === '') {
            setCubicMeter('');
            setLiter('');
            setGallons('');
            setCubicFeet('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / volumeConversionFactors.barrel;
            setCubicMeter(formatNumber(baseValue, 6));
            setLiter(formatNumber(baseValue * volumeConversionFactors.liter, 6));
            setGallons(formatNumber(baseValue * volumeConversionFactors.gallons, 6));
            setCubicFeet(formatNumber(baseValue * volumeConversionFactors.cubicFeet, 6));
        }
    };

    return (
        <TabContent>
            <ConversionTable>
                <thead>
                    <tr>
                        <th>Volume</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Cubic Meter:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="cubicmeter"
                                        type="text"
                                        value={cubicMeter}
                                        onChange={(e) => handleCubicMeterChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="cubicmeter">m³</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Liter:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput id="liter" type="text" value={liter} onChange={(e) => handleLiterChange(e.target.value)} />
                                    <InputLabel htmlFor="liter">l</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Gallons:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput id="gallons" type="text" value={gallons} onChange={(e) => handleGallonsChange(e.target.value)} />
                                    <InputLabel htmlFor="gallons">gal</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Cubic Feet:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="cubicFeet"
                                        type="text"
                                        value={cubicFeet}
                                        onChange={(e) => handleCubicFeetChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="cubicFeet">ft³</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Barrel:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput id="barrel" type="text" value={barrel} onChange={(e) => handleBarrelChange(e.target.value)} />
                                    <InputLabel htmlFor="barrel">bbl</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                </tbody>
            </ConversionTable>
        </TabContent>
    );
};

export default VolumeConverter;
